import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV_LIVE as NAV, LOGO, isExternal, CLIENT_LOGIN_HREF } from "./nav";

export function SmartLink({ href, className, children, ...rest }) {
  if (isExternal(href)) {
    return <a href={href} className={className} target="_blank" rel="noopener" {...rest}>{children}</a>;
  }
  return <Link to={href} className={className} {...rest}>{children}</Link>;
}

// Opens Calendly popup when the widget script is loaded; falls back to the link.
export function CalendlyLink({ className, children, testId, context }) {
  // Opens the branded booking widget (BookingDrawerHost); /book/ stays the no-JS fallback.
  const onClick = (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("hm:book", { detail: { path: window.location.pathname, context } }));
  };
  return (
    <a className={className} href="/book/" onClick={onClick} data-testid={testId}>
      {children}
    </a>
  );
}

function MenuItems({ item, onNavigate, placement = 'desktop' }) {
  const testPrefix = `nav-${placement}-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <>
      {item.groups.map((g, i) => (
        <div key={i}>
          {g.label && <div className="menu-label">{g.label}</div>}
          {g.items.map(([label, href, desc]) => (
            <SmartLink key={label} href={href} onClick={onNavigate} className={desc ? "has-desc" : undefined} data-testid={`${testPrefix}-${i}-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{label}{desc && <small>{desc}</small>}</SmartLink>
          ))}
        </div>
      ))}
      {item.callout && (
        <div className="menu-callout" style={{ gridColumn: "1 / -1" }}>
          <strong>{item.callout.title}</strong>{item.callout.text}{" "}
          <SmartLink href={item.callout.href} className="textlink" onClick={onNavigate} data-testid={`${testPrefix}-callout-link`}>{item.callout.cta || "Learn more"}</SmartLink>
        </div>
      )}
    </>
  );
}

function DesktopItem({ item, open, setOpen }) {
  const id = `menu-${item.label.replace(/\W+/g, "-").toLowerCase()}`;
  const closeTimer = useRef(null);
  const enter = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const leave = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), 220); };
  return (
    <div className="navitem" data-open={open} onMouseEnter={enter} onMouseLeave={leave}>
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => { clearTimeout(closeTimer.current); setOpen(true); }} data-testid={`nav-toggle-${item.label.toLowerCase().replace(/\W+/g, "-")}`}>
        {item.label}
      </button>
      <div id={id} className={`menu${item.wide ? " wide" : ""}${item.cols === 3 ? " cols-3" : ""}`} role="region" aria-label={item.label}>
        <MenuItems item={item} onNavigate={() => setOpen(false)} />
      </div>
    </div>
  );
}

export default function SiteHeader() {
  const [openIdx, setOpenIdx] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => { setOpenIdx(null); setMobileOpen(false); }, [location.pathname]);
  useEffect(() => { document.body.style.overflow = mobileOpen ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [mobileOpen]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setOpenIdx(null); setMobileOpen(false); } };
    const onClick = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpenIdx(null); };
    document.addEventListener("keydown", onKey); document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, []);

  return (
    <header>
      <nav className="nav wrap" aria-label="Main navigation" ref={navRef}>
        <Link to="/" data-testid="site-logo-link"><img className="logo" src={LOGO} alt="Hello Mortgage Team, powered by BRX Mortgage" /></Link>
        <div className="navlinks">
          {NAV.map((item, i) => (
            <DesktopItem key={item.label} item={item} open={openIdx === i} setOpen={(v) => setOpenIdx((cur) => (v ? i : cur === i ? null : cur))} />
          ))}
          <Link className="partner-login" to={CLIENT_LOGIN_HREF} data-testid="nav-client-login">Client Login</Link>
          <CalendlyLink className="btn nav-cta" testId="nav-cta-lets-talk">Let's Talk</CalendlyLink>
          <button type="button" className="nav-toggle" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} aria-controls="mobile-nav" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
            {mobileOpen ? "×" : "☰"}
          </button>
        </div>
      </nav>
      <div id="mobile-nav" className="mobile-nav" data-open={mobileOpen}>
        <div className="wrap">
          {NAV.map((item, i) => (
            <div className="mobile-group" key={item.label}>
              <button type="button" aria-expanded={mobileGroup === i} onClick={() => setMobileGroup(mobileGroup === i ? null : i)} data-testid={`mobile-nav-group-${item.label.toLowerCase().replace(/\W+/g, "-")}`}>
                {item.label}<span className="plus" aria-hidden="true">+</span>
              </button>
              <div className="mobile-links"><MenuItems item={item} placement="mobile" onNavigate={() => setMobileOpen(false)} /></div>
            </div>
          ))}
          <div className="mobile-actions">
            <CalendlyLink className="btn" testId="mobile-nav-cta">Let's Talk</CalendlyLink>
            <Link className="btn outline" to={CLIENT_LOGIN_HREF} data-testid="mobile-nav-client-login">Client Login</Link>
          </div>
        </div>
      </div>
    </header>
  );
}
